import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { Form, Button, Spinner, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { Link, Redirect } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Helmet } from 'react-helmet-async';
import { trackSignUp } from '../utils/analytics';

const Register: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Redirect to="/dashboard" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await register(firstName, lastName, email, password);
      trackSignUp();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage><IonContent>
    <main className="container py-4" style={{ maxWidth: 440, margin: '0 auto' }}>
      <Helmet>
        <title>Create a Free Account | QA Master</title>
        <meta name="description" content="Create a free QA Master account: 1 project, 1 test suite and up to 10 AI-generated automated tests, no card needed." />
        <link rel="canonical" href="https://qamaster.uk/register" />
      </Helmet>
      <div>
              <div style={{ textAlign: 'center', marginTop: 40, marginBottom: 32 }}>
                <h1 style={{
                  fontSize: 32, fontWeight: 700, margin: 0,
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                }}>QA Master</h1>
                <p style={{ color: '#666', marginTop: 8 }}>Create your account</p>
              </div>

        <div className="bg-white rounded-3 shadow-sm p-4">
          <form onSubmit={handleSubmit}>
            {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label className="fw-semibold small">First Name</Form.Label>
                  <Form.Control
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    required
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group>
                  <Form.Label className="fw-semibold small">Last Name</Form.Label>
                  <Form.Control
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">Email</Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold small">Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold small">Confirm Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                />
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => setShowConfirmPassword(s => !s)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeSlash /> : <Eye />}
                </Button>
              </InputGroup>
            </Form.Group>

            <Button
              type="submit"
              className="w-100 btn-brand-gradient fw-semibold"
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : 'Create Account'}
            </Button>
          </form>
        </div>

        <p className="text-center text-muted mt-4 small">
          Already have an account?{' '}
          <Link to="/login" className="fw-semibold text-decoration-none">Sign in</Link>
        </p>
        <p className="text-center mt-2">
          <Link to="/" className="text-secondary small text-decoration-none">Back to home</Link>
        </p>
      </div>
    </main>
    </IonContent></IonPage>
  );
};

export default Register;
